import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FALLBACK_LOCALE, localeDescriptor, offeredLocales,
  applyDocumentLocale, resolveInitialLocale, storeLocale, normalizeLocale,
} from './config';
import { loadCatalog, peekCatalog, preloadCatalog } from './loader';
import { createTranslator } from './translator';
import { createFormatters } from './format';
import { bindMoney, DEFAULT_CURRENCY } from './money';

// ─────────────────────────────────────────────────────────────────────────────
// The one i18n context. Everything a screen needs — `t`, the formatters, the
// direction, the way to change language — comes from here and nowhere else.
//
// It is deliberately shaped like the design system's ThemeProvider, because it
// solves the same problem and should be read the same way:
//
//   1. localStorage, read synchronously during the first render, so a reload
//      paints the right language AND the right direction immediately instead of
//      flashing Persian at an English-speaking user.
//   2. The server copy (the user's `locale` attribute in Keycloak), pushed in
//      once `me` resolves. That is the real record and wins whenever it differs
//      — it is how the choice follows a staff member to the second desk.
//
// Switching language NEVER unmounts anything. The new catalogue is loaded, then
// swapped in one state update, so the router, every open dialog and every
// half-filled form survive the switch — requirement 7, and the reason this is a
// context value rather than a remount with a `key`.
// ─────────────────────────────────────────────────────────────────────────────

const I18nContext = createContext(null);

export { I18nContext };

export function I18nProvider({ language, children, fallback = null }) {
  // Lazy initialiser — runs before first paint, not in an effect, so <html dir>
  // is correct for the very first frame.
  const [locale, setLocale] = useState(() => applyDocumentLocale(resolveInitialLocale()));

  // The catalogues currently being rendered. `null` until the first load lands;
  // on a LATER switch the previous catalogue keeps rendering until the new one
  // is ready, so there is no flash of untranslated keys.
  const [catalogs, setCatalogs] = useState(() => {
    const active = peekCatalog(locale);
    if (!active) return null;
    const fb = locale === FALLBACK_LOCALE ? active : peekCatalog(FALLBACK_LOCALE);
    return fb ? { locale, active, fallback: fb } : null;
  });

  // Guards against an out-of-order resolution when the user switches twice
  // quickly: only the most recently requested locale may commit.
  const requested = useRef(locale);

  useEffect(() => {
    requested.current = locale;
    let live = true;

    // The fallback catalogue is loaded alongside the active one so a key
    // missing from a translation can resolve to English synchronously, inside
    // `t`, rather than triggering a load mid-render.
    Promise.all([
      loadCatalog(locale),
      locale === FALLBACK_LOCALE ? null : loadCatalog(FALLBACK_LOCALE),
    ]).then(([active, fb]) => {
      if (!live || requested.current !== locale) return;
      setCatalogs({ locale, active, fallback: fb || active });
    });

    return () => { live = false; };
  }, [locale]);

  // Adopt the server's copy when it arrives or changes (e.g. the user changed
  // language on another device). `null`/`undefined` means "not loaded yet" —
  // NOT "reset to default" — so an in-flight `me` query never clobbers a choice
  // the user just made. An unrecognised tag is ignored rather than applied:
  // a language this build does not ship must not blank the UI. Nor is one the
  // brand does not offer applied — the account's own default («fa») included.
  const serverLanguage = normalizeLocale(language);
  useEffect(() => {
    if (!serverLanguage) return;
    setLocale((prev) => {
      if (prev === serverLanguage) return prev;
      storeLocale(serverLanguage);
      return applyDocumentLocale(serverLanguage);
    });
  }, [serverLanguage]);

  /**
   * Apply a language locally and cache it. Two callers:
   *   - the settings picker, so the UI switches under the user's cursor
   *     instead of after a server round trip;
   *   - app boot, to adopt the server's copy once `myProfile` resolves.
   * Persisting to the BFF is the caller's job — this only paints and caches.
   */
  const changeLocale = useCallback((next) => {
    const id = normalizeLocale(next);
    if (!id) return;
    setLocale((prev) => {
      if (prev === id) return prev;
      storeLocale(id);
      return applyDocumentLocale(id);
    });
  }, []);

  const value = useMemo(() => {
    const descriptor = localeDescriptor(locale);
    const formatters = createFormatters(locale);

    // Catalogues for a DIFFERENT locale than `locale` means a switch is in
    // flight: keep translating with what we have. `t` still resolves — it just
    // resolves in the outgoing language for the frame or two it takes the new
    // catalogue to arrive from cache or network.
    const active = catalogs?.active || {};
    const fb = catalogs?.fallback || {};
    const renderedLocale = catalogs?.locale || locale;

    const pluralRules = typeof Intl.PluralRules === 'function'
      ? new Intl.PluralRules(localeDescriptor(renderedLocale).intl)
      : null;

    const { t, tList, has } = createTranslator({
      locale: renderedLocale,
      catalog: active,
      fallbackCatalog: fb,
      pluralRules,
      joinList: formatters.formatList,
      formatNumber: formatters.formatNumber,
    });

    // Clinic money, bound to IRR here: this provider sits ABOVE the clinic
    // context (the sign-in and onboarding screens need copy before any clinic is
    // known), so it cannot know the clinic's currency. <CurrencyProvider>,
    // rendered by ClinicContextProvider, re-binds all of this to the clinic's.
    const money = bindMoney({ t, tList, formatMoney: formatters.formatMoney, locale }, DEFAULT_CURRENCY);

    return {
      // identity
      locale,
      dir: descriptor.dir,
      isRTL: descriptor.dir === 'rtl',
      // Whether this reader can read the Persian clinical reference data. NOT
      // implied by `isRTL` — Arabic shares the direction and not the language.
      readsPersianData: descriptor.readsPersianData === true,
      // What a language picker lists: every shipped language, or only the
      // ones the brand offers.
      locales: offeredLocales(),
      // translation
      t, tList, has,
      // formatting
      ...formatters,
      // money: currency, formatMoney, moneyUnit, moneyAmount, toDisplayAmount,
      // fromDisplayAmount, parseMoney — and `t`/`tList` whose {{unit}} and
      // {{unitShort}} default to the bound currency's word (money.js)
      ...money,
      // The unbound pieces, for <CurrencyProvider> to re-bind from.
      moneyBase: { t, tList, formatMoney: formatters.formatMoney, locale },
      // control
      setLocale: changeLocale,
      preloadLocale: preloadCatalog,
      // true once the active locale's catalogue is the one being rendered
      ready: catalogs?.locale === locale,
    };
  }, [locale, catalogs, changeLocale]);

  // Only the FIRST load blocks — after that `catalogs` is never null again, so
  // a language switch re-renders in place and the navigation state survives.
  if (!catalogs) return fallback;

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export default I18nProvider;
